import { useEffect, useState } from 'react';
import { listOnsiteLiveUsers } from '@/api/onsiteLiveAdmin';
import { EntityPicker } from '@/components/EntityPicker';
import { FieldError } from '../BranchRelationships/shared';
import { btnSecondary, inputCls } from '../BranchRelationships/styles';

/**
 * Pick an OnsiteLive user by name instead of typing a UserId (external and
 * replacement manager; Anthony, 25/10/2026). The value is still the legacy
 * UserId - OnFlow walks it - so the form field stays a string id ('' = none).
 * Only active users of active, not-terminated Oscar people are offered, each
 * with their title and location (picker mode of the list endpoint).
 */
export function OnsiteLiveUserPicker({
  label, value, valueLabel, onChange, canWrite, excludeId, hint, error,
}: {
  label: string;
  /** The UserId as text; '' = none. */
  value: string;
  /** The current user's name from the API, if any. */
  valueLabel?: string | null;
  /** The UserId as text ('' = cleared), and the chosen person's name. */
  onChange: (value: string, label?: string) => void;
  canWrite: boolean;
  /** The account being edited: a user cannot be their own manager. */
  excludeId?: number;
  hint?: string | null;
  error?: string;
}) {
  const [name, setName] = useState(valueLabel ?? '');
  useEffect(() => { setName(valueLabel ?? ''); }, [valueLabel]);
  const shown = value ? (name || `UserId ${value}`) : '';

  return (
    <div className="flex flex-col text-xs font-semibold text-darkGrey">
      <span>{label}</span>
      {canWrite ? (
        <div className="mt-1 flex items-center gap-2 font-normal">
          <div className="flex-1">
            <EntityPicker
              queryKey={`onsitelive-user-picker-${label}`}
              search={async (q, signal) =>
                (await listOnsiteLiveUsers({ search: q, picker: true }, signal))
                  .filter((u) => u.OnsiteLiveUserId !== excludeId)
                  .slice(0, 20)
                  .map((u) => ({
                    id: u.OnsiteLiveUserId,
                    label: u.PersonDisplayName ?? u.FullName ?? u.Username,
                    sublabel: [u.Title, u.Location].filter(Boolean).join(' · ') || u.Username,
                  }))}
              valueLabel={shown}
              onChange={(id, text) => { setName(text); onChange(id == null ? '' : String(id), text); }}
              placeholder={`${label} - search by name or username`}
              inputClassName={`${inputCls} w-full font-normal`}
            />
          </div>
          {value && (
            <button type="button" className={btnSecondary} onClick={() => { setName(''); onChange(''); }} aria-label={`Clear ${label.toLowerCase()}`}>
              Clear
            </button>
          )}
        </div>
      ) : (
        <span className="mt-1 text-sm font-normal text-darkGrey/80">{shown || '—'}</span>
      )}
      {value && <span className="mt-1 font-normal text-darkGrey/60">UserId {value}</span>}
      {hint && <span className="mt-1 font-normal text-darkGrey/70">{hint}</span>}
      {error && <FieldError message={error} />}
    </div>
  );
}
